import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { baseUrl } from "../utils/utils";

// Fetch notification list with proper limit
export const get_notifications = createAsyncThunk(
  "notifications/list",
  async (data) => {
    const body = new FormData();
    body.append("action", "list");
    body.append("page", data?.page || 1);
    body.append("limit", data?.limit || 30); // ✅ Default 30 notifications
    body.append("search", data?.search || "");

    const response = await fetch(baseUrl + "notifications.php", {
      method: "POST",
      body,
    });
    const res = await response.json();
    return res;
  }
);

// Mark notification as seen
export const mark_notification_seen = createAsyncThunk(
  "notifications/seen",
  async (id) => {
    const body = new FormData();
    body.append("id", id);
    body.append("status", "1");

    const response = await fetch(baseUrl + "update_notify_seen.php", {
      method: "POST",
      body,
    });
    const res = await response.json();
    return { id, success: res.success };
  }
);

// Mark all notifications as read
export const mark_all_notifications_read = createAsyncThunk(
  "notifications/markAllRead",
  async () => {
    const body = new FormData();
    body.append("action", "mark_all_read");

    const response = await fetch(baseUrl + "notifications.php", {
      method: "POST",
      body,
    });
    const res = await response.json();
    return res;
  }
);

// LocalStorage helpers
const getLastSeenNotificationIds = () => {
  try {
    const stored = localStorage.getItem("last_seen_notification_ids");
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
};

const saveLastSeenNotificationIds = (ids) => {
  try {
    localStorage.setItem("last_seen_notification_ids", JSON.stringify(ids));
  } catch {}
};

const initialState = {
  notifications: [],
  loading: false,
  total_unseen: 0,
  last_seen_notification_ids: getLastSeenNotificationIds(),
  lastFetchTime: null, // ✅ Track last fetch time
};

const notificationSlice = createSlice({
  name: "notifications",
  initialState,
  reducers: {
    markNotificationsVisited: (state) => {
      const currentIds = state.notifications.map((n) => n.id);
      state.last_seen_notification_ids = currentIds;
      saveLastSeenNotificationIds(currentIds);
      state.total_unseen = 0;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(get_notifications.pending, (state) => {
        state.loading = true;
      })
      .addCase(get_notifications.rejected, (state) => {
        state.loading = false;
      })
      .addCase(get_notifications.fulfilled, (state, action) => {
        state.loading = false;
        state.lastFetchTime = Date.now(); // ✅ Update fetch time
        
        const data = action.payload;
        if (data.success) {
          // ✅ Only store first 30 notifications max
          state.notifications = (data.data || []).slice(0, 30);
          
          // Use API counts for unseen (more accurate)
          state.total_unseen = parseInt(data.totalunseencount || "0", 10);
        } else {
          state.notifications = [];
          state.total_unseen = 0;
        }
      })

      // ✅ Decrease unseen count dynamically
      .addCase(mark_notification_seen.fulfilled, (state, action) => {
        if (action.payload.success) {
          const id = action.payload.id;
          const index = state.notifications.findIndex((n) => n.id === id);
          if (index !== -1 && state.notifications[index].seen_status === "0") {
            state.notifications[index].seen_status = "1";
            if (state.total_unseen > 0) {
              state.total_unseen -= 1;
            }
          }
        }
      })

      .addCase(mark_all_notifications_read.fulfilled, (state, action) => {
        if (action.payload.success) {
          state.notifications = state.notifications.map((n) => ({
            ...n,
            seen_status: "1",
          }));
          state.total_unseen = 0;
        }
      });
  },
});

export const { markNotificationsVisited } = notificationSlice.actions;
export default notificationSlice.reducer;